import { useEffect, useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { LIGHTBOX } from '@/constants/testIds';
import { LightboxActionBar } from '@/components/lightbox/LightboxActionBar';
import { LightboxNotesPanel } from '@/components/lightbox/LightboxNotesPanel';

const navButtonClass = 'absolute top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-black/50 text-[hsl(var(--lxra-gold))] hover:bg-black/75 transition-colors';

/**
 * Full-screen image viewer with keyboard navigation.
 *
 * `favorites`/`selections` (Sets of image ids), `onToggleFavorite(image)`,
 * `onToggleSelection(image)`, `onDownload(image, resolution)` and comment
 * props are all optional. `onDownload` alone (without favorites/selections)
 * renders a view-only download action — used for no-login share links.
 */
export const Lightbox = ({
  images,
  index,
  onClose,
  onNavigate,
  favorites,
  onToggleFavorite,
  onDownload,
  selections,
  onToggleSelection,
  selectionLimit,
  comments,
  onAddComment,
}) => {
  const open = index !== null && index !== undefined;
  const [notesOpen, setNotesOpen] = useState(false);

  const goPrev = useCallback(() => {
    if (index === null) return;
    onNavigate((index - 1 + images.length) % images.length);
  }, [index, images.length, onNavigate]);

  const goNext = useCallback(() => {
    if (index === null) return;
    onNavigate((index + 1) % images.length);
  }, [index, images.length, onNavigate]);

  // Close the notes drawer whenever the photo changes.
  useEffect(() => { setNotesOpen(false); }, [index]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, goPrev, goNext, onClose]);

  if (!open) return null;

  const current = images[index];
  const isFavorite = Boolean(favorites?.has(current?.id));
  const isSelected = Boolean(selections?.has(current?.id));
  const showActions = Boolean(onToggleFavorite || onDownload || onToggleSelection);
  const currentComments = comments?.[current?.id] || [];
  const limitReached = selectionLimit != null && selections && selections.size >= selectionLimit && !isSelected;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={LIGHTBOX.dialog} hideClose className="max-w-5xl w-[92vw] bg-black/95 border-[hsl(var(--lxra-border))] p-0 overflow-hidden">
        <div className="relative flex items-center justify-center min-h-[50vh] max-h-[85vh]">
          <DialogTitle className="sr-only">{current?.caption || 'Gallery image preview'}</DialogTitle>
          <img src={current?.image_url} alt={current?.caption || 'Gallery image'} className="max-h-[85vh] w-full object-contain" />

          <button onClick={onClose} aria-label="Close" data-testid={LIGHTBOX.close} className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center rounded-full bg-black/60 text-[hsl(var(--lxra-gold))] hover:bg-black/80 transition-colors">
            <X className="h-5 w-5" />
          </button>
          <button onClick={goPrev} aria-label="Previous image" data-testid={LIGHTBOX.prev} className={`left-3 ${navButtonClass}`}>
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={goNext} aria-label="Next image" data-testid={LIGHTBOX.next} className={`right-3 ${navButtonClass}`}>
            <ChevronRight className="h-5 w-5" />
          </button>

          {showActions && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 max-w-[92%]">
              <LightboxActionBar
                image={current}
                isFavorite={isFavorite}
                isSelected={isSelected}
                limitReached={limitReached}
                selectionLimit={selectionLimit}
                commentCount={currentComments.length}
                notesOpen={notesOpen}
                onToggleFavorite={onToggleFavorite}
                onToggleSelection={onToggleSelection}
                onToggleNotes={onAddComment ? () => setNotesOpen((v) => !v) : undefined}
                onDownload={onDownload}
              />
              {notesOpen && onAddComment && (
                <LightboxNotesPanel key={current?.id} image={current} comments={currentComments} onAddComment={onAddComment} />
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
